import React, { useState } from 'react'
import { Check, Clock, Eye, Loader2, ShieldCheck, Trophy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { viewsLabel } from '@adthlete/common/utils/dealPricing'
import {
  useExtendLicenseAtRateCardMutation,
  useGetLicenseOptionsQuery,
  type DealPerformance,
} from '@/redux/marketerApi'

const money = (n: number) => `$${n.toLocaleString('en-US')}`
const compact = (n: number) =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n)

/**
 * A gifting deal at a glance for the brand: how far the post has gone, which bonus milestones it
 * has unlocked (and what is next), and how long the brand can still run it as an ad, with a
 * one-click extension priced from the athlete's rate card.
 */
export const DealPerformanceCard: React.FC<{
  collaborationId: string
  athleteName: string
  performance: DealPerformance
}> = ({ collaborationId, athleteName, performance: p }) => {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const { data: options } = useGetLicenseOptionsQuery(collaborationId, { skip: !open })
  const [extend, { isLoading }] = useExtendLicenseAtRateCardMutation()
  const first = athleteName.split(' ')[0]
  if (!p.terms) return null
  const unlocked = new Set(p.bonus?.unlocked.map((t) => t.views))

  const doExtend = async (days: number, label: string, price: number) => {
    try {
      await extend({ collaborationId, days }).unwrap()
      setOpen(false)
      toast({
        title: `License extended ${label}`,
        description: `${money(price)} at ${first}'s listed price, invoiced separately. ${first} will send the ad codes.`,
      })
    } catch (err: any) {
      toast({
        title: "Couldn't extend",
        description: err?.data?.error ?? 'Try again in a moment.',
        variant: 'destructive',
      })
    }
  }

  return (
    <section className="rounded-2xl border border-foreground/10 bg-card p-5 sm:p-6 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
        {/* Bonuses */}
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-semibold text-foreground">
            <Trophy className="h-4 w-4 text-[#B45309]" /> Performance bonuses
          </p>
          {p.posted ? (
            <p className="mt-1 text-sm text-gray-500">
              <Eye className="mr-1 inline h-4 w-4" />
              Best post:{' '}
              <span className="font-semibold text-foreground">{compact(p.views)} views</span>
              {p.bonus?.next
                ? ` · ${compact(p.bonus.next.views - p.views)} more to the next bonus`
                : ' · every milestone reached'}
            </p>
          ) : (
            <p className="mt-1 text-sm text-gray-500">
              Bonuses unlock once {first}'s post is live.
            </p>
          )}
          <ol className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
            {p.terms.bonuses.map((t) => {
              const done = unlocked.has(t.views)
              return (
                <li
                  key={t.views}
                  className={cn(
                    'rounded-lg border px-2 py-2 text-center',
                    done ? 'border-[#C0FD72] bg-[#C0FD72]/15' : 'border-foreground/10',
                  )}
                >
                  <span className="block text-xs text-gray-500">{viewsLabel(t.views)}</span>
                  <span className="block font-semibold tabular-nums text-foreground">
                    {money(t.amount)}
                  </span>
                  {done ? <Check className="mx-auto mt-0.5 h-3.5 w-3.5 text-[#3F7A0A]" /> : null}
                </li>
              )
            })}
          </ol>
          <p className="mt-3 text-sm text-gray-500">
            Earned so far:{' '}
            <span className="font-semibold text-foreground">{money(p.bonus?.total ?? 0)}</span> of
            up to {money(p.bonus?.max ?? 0)} · Athios fee {money(p.terms.activationFee)}
          </p>
        </div>

        {/* Paid usage */}
        <div className="rounded-xl border border-foreground/10 p-4">
          <p className="flex items-center gap-2 font-semibold text-foreground">
            <ShieldCheck className="h-4 w-4 text-[#0E7490]" /> Paid usage
          </p>
          {p.usage.endsAt ? (
            <>
              <p className="mt-2 text-2xl font-bold tabular-nums text-foreground">
                {p.usage.daysLeft} days left
              </p>
              <p className="text-xs text-gray-500">
                Ends{' '}
                {new Date(p.usage.endsAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
                {p.usage.extensions.length
                  ? ` · ${p.usage.includedDays} included + ${p.usage.extensions.reduce((s, e) => s + e.termDays, 0)} extended`
                  : ` · ${p.usage.includedDays} days included`}
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-gray-500">
              {p.terms.usageDays} days start when the post goes live.
            </p>
          )}
          <Button
            variant="lime"
            size="sm"
            className="mt-3 w-full"
            disabled={!p.posted}
            onClick={() => setOpen(true)}
          >
            <Clock className="h-4 w-4" /> Extend license
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-foreground/10 bg-card text-foreground sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Extend paid usage</DialogTitle>
            <DialogDescription className="text-gray-600">
              {options?.fromRateCard
                ? `Priced from ${first}'s shop, so it's agreed already.`
                : `${first} hasn't set prices yet; these are Athios's suggested prices.`}{' '}
              {first} gets a task to send the Spark / partnership code.
            </DialogDescription>
          </DialogHeader>
          {options ? (
            <ul className="space-y-2">
              {options.options.map((o) => (
                <li key={o.days}>
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => doExtend(o.days, o.label, o.price)}
                    className="flex w-full items-center justify-between rounded-xl border border-foreground/10 px-4 py-3 text-left hover:bg-foreground/10 disabled:opacity-50"
                  >
                    <span className="font-semibold text-foreground">+{o.label}</span>
                    <span className="font-bold tabular-nums text-foreground">
                      {money(o.price)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-600" />
          )}
        </DialogContent>
      </Dialog>
    </section>
  )
}

export default DealPerformanceCard
